// frontend-react/src/components/ReviewResults.tsx
import React, { useState } from 'react';
import { Button, Input, Select } from 'antd';
import {
  CheckOutlined,
  CloseOutlined,
  DeleteOutlined,
  EditOutlined,
  ReloadOutlined,
} from '@ant-design/icons';
import TextHighlight from './TextHighlight';
import { formatTimestamp } from '../utils/formatTimestamp';
import { getRiskLevel } from '../pages/adReview/adReviewUtils';
import type { RiskLevel } from '../types';
import { generateReviewCsvString } from '../utils/csvUtils';

type FilterState = null | 'high' | 'medium' | 'low';

const FILTER_CYCLE: FilterState[] = [null, 'high', 'medium', 'low'];

const FILTER_CONFIG: Record<
  'none' | 'high' | 'medium' | 'low',
  { color: string; label: string; bg: string }
> = {
  none: { color: '#adb5bd', label: '全部', bg: 'rgba(173,181,189,0.12)' },
  high: { color: '#c0392b', label: '高風險', bg: 'rgba(192,57,43,0.10)' },
  medium: { color: '#d4a017', label: '中風險', bg: 'rgba(212,160,23,0.10)' },
  low: { color: '#27ae60', label: '低風險', bg: 'rgba(39,174,96,0.10)' },
};

interface ReviewItem {
  item_id: string;
  _uid: string;
  page: string;
  line?: string;
  rule: string;
  origin: string;
  suggestion: string;
  risk_level?: 'high' | 'medium' | 'low';
}

interface ReviewResultsProps {
  results: ReviewItem[];
  isLoading: boolean;
  onDeleteCard?: ((itemId: string) => void) | undefined;
  onEditCard?: ((itemId: string, changes: Partial<ReviewItem>) => void) | undefined;
  onRestoreCard?: ((itemId: string) => void) | undefined;
  editedItemIds?: Set<string> | undefined;
  fileName?: string | undefined;
  showRiskLevel?: boolean;
  hideSuggestionLabel?: boolean;
  ruleOptions?: { id: string; name: string }[] | undefined;
}

const ReviewResults: React.FC<ReviewResultsProps> = ({
  results,
  isLoading,
  onDeleteCard,
  onEditCard,
  onRestoreCard,
  editedItemIds,
  fileName,
  showRiskLevel = true,
  hideSuggestionLabel = false,
  ruleOptions,
}) => {
  const [filter, setFilter] = useState<FilterState>(null);

  const parseSuggestion = (text: string): { origin: string; explanation: string } | null => {
    for (const om of ['原稿：', '原稿:']) {
      const oi = text.indexOf(om);
      if (oi === -1) continue;
      for (const em of ['說明：', '說明:']) {
        const ei = text.indexOf(em, oi);
        if (ei === -1) continue;
        return {
          origin: text.slice(oi + om.length, ei).trim(),
          explanation: text.slice(ei + em.length).trim(),
        };
      }
    }
    return null;
  };
  const [editingUid, setEditingUid] = useState<string | null>(null);
  const [editDraft, setEditDraft] = useState<{
    item_id: string;
    rule: string;
    origin: string;
    suggestion: string;
  }>({ item_id: '', rule: '', origin: '', suggestion: '' });

  const cycleFilter = () => {
    const currentIndex = FILTER_CYCLE.indexOf(filter);
    const next = FILTER_CYCLE[(currentIndex + 1) % FILTER_CYCLE.length];
    setFilter(next ?? null);
  };

  const filterKey: 'none' | 'high' | 'medium' | 'low' = filter ?? 'none';
  const filterCfg = FILTER_CONFIG[filterKey];
  const filteredResults =
    showRiskLevel && filter ? results.filter(item => item.risk_level === filter) : results;
  const getRiskLevelClass = (level: RiskLevel): string => {
    return `risk-${level}`;
  };

  const handleJumpToText = (item: ReviewItem) => {
    const riskLevel = getRiskLevel(item.risk_level);

    // Use global jumpToText function from PDFViewer
    // 注意：傳遞簡單的風險等級字符串，不是CSS類名
    if (typeof window !== 'undefined' && (window as any).jumpToText) {
      (window as any).jumpToText(item.page, item.origin, riskLevel);
    }
  };

  const createHighlightRanges = (text: string, riskLevel: RiskLevel) => {
    return [
      {
        start: 0,
        end: text.length,
        type: `risk-${riskLevel}`, // 保留不同類型，讓顏色可以依照等級不同
        label: '審查項目',
        color: getRiskLevelColor(riskLevel),
        attributes: {
          category: '法規審查',
        },
      },
    ];
  };

  const getRiskLevelColor = (level: RiskLevel): string => {
    switch (level) {
      case 'high':
        return 'rgba(159, 53, 58, 0.2)'; // 燕脂
      case 'medium':
        return 'rgba(252, 186, 77, 0.2)'; // 琥珀
      case 'low':
        return 'rgba(27, 129, 62, 0.2)'; // 常磐
      default:
        return 'rgba(252, 186, 77, 0.2)'; // 琥珀（歷史資料）
    }
  };

  const downloadCSV = () => {
    if (!results.length) return;
    const csvContent = generateReviewCsvString(results);
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    const baseName = fileName ? fileName.replace(/\.(pdf|docx)$/i, '') : 'reviewed';
    a.download = `review_${baseName}_${formatTimestamp()}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  // SonarQube Fix: Handle keyboard events for non-native interactive elements
  const handleCardKeyDown = (e: React.KeyboardEvent, item: ReviewItem) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleJumpToText(item);
    }
  };

  if (isLoading) {
    return (
      <div className='loading-state'>
        <div className='loading-spinner'></div>
        AI 正在分析...
      </div>
    );
  }

  if (!results.length) {
    return (
      <div className='empty-state'>
        <div className='empty-state-icon'>🔍</div>
        <div>等待文件上傳後生成審閱報告</div>
        <div style={{ fontSize: '12px', marginTop: '8px' }}>AI 將自動檢查合規風險並提供建議</div>
      </div>
    );
  }

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '8px',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          {showRiskLevel && (
            <button
              onClick={cycleFilter}
              title={`目前篩選：${filterCfg.label}，點擊切換`}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                padding: '3px 10px',
                border: `1px solid ${filterCfg.color}`,
                borderRadius: '12px',
                background: filterCfg.bg,
                cursor: 'pointer',
                fontSize: '12px',
                color: filterCfg.color,
                fontWeight: 500,
                transition: 'all 0.2s',
              }}
            >
              <span
                style={{
                  width: '10px',
                  height: '10px',
                  borderRadius: '50%',
                  background: filterCfg.color,
                  display: 'inline-block',
                  flexShrink: 0,
                }}
              />
              {filterCfg.label}
            </button>
          )}
          <div style={{ fontSize: '14px', color: '#656d76' }}>
            {showRiskLevel && filter
              ? `${filterCfg.label}：${filteredResults.length} / ${results.length} 個`
              : `總共 ${results.length} 個審閱建議`}
          </div>
        </div>
        <Button size='small' onClick={downloadCSV}>
          下載 CSV
        </Button>
      </div>

      <div className='review-results-container' style={{ flex: 1 }}>
        {filteredResults.map(item => {
          const originalIndex = results.indexOf(item);
          const riskLevel = getRiskLevel(item.risk_level);
          const riskLevelClass = getRiskLevelClass(riskLevel);
          const ruleLabel = `第${item.item_id}條 ${item.rule}`;

          return (
            <div
              key={`${item.item_id}-${originalIndex}`}
              className={`risk-item-card ${showRiskLevel ? riskLevelClass : 'risk-medium'}`}
              onClick={() => {
                if (editingUid !== item._uid) handleJumpToText(item);
              }}
              // SonarQube Fix: Add accessibility attributes for interactive div
              role='button'
              tabIndex={0}
              onKeyDown={e => {
                if (editingUid !== item._uid) handleCardKeyDown(e, item);
              }}
              style={{ cursor: 'pointer', position: 'relative' }}
            >
              {/* 編輯確認/取消按鈕（編輯模式下顯示） */}
              {onEditCard && editingUid === item._uid && (
                <>
                  <button
                    className='card-edit-btn card-edit-confirm-btn'
                    onClick={e => {
                      e.stopPropagation();
                      const changes: Partial<ReviewItem> = {};
                      if (editDraft.item_id.trim() && editDraft.item_id.trim() !== item.item_id)
                        changes.item_id = editDraft.item_id.trim();
                      if (editDraft.rule.trim() && editDraft.rule.trim() !== item.rule)
                        changes.rule = editDraft.rule.trim();
                      if (editDraft.origin.trim() && editDraft.origin.trim() !== item.origin)
                        changes.origin = editDraft.origin.trim();
                      if (
                        editDraft.suggestion.trim() &&
                        editDraft.suggestion.trim() !== item.suggestion
                      )
                        changes.suggestion = editDraft.suggestion.trim();
                      if (Object.keys(changes).length > 0) onEditCard(item._uid, changes);
                      setEditingUid(null);
                    }}
                    aria-label='確認編輯'
                    title='確認'
                  >
                    <CheckOutlined />
                  </button>
                  <button
                    className='card-edit-btn card-edit-cancel-btn'
                    onClick={e => {
                      e.stopPropagation();
                      setEditingUid(null);
                    }}
                    aria-label='取消編輯'
                    title='取消'
                  >
                    <CloseOutlined />
                  </button>
                </>
              )}
              {/* 還原按鈕（已編輯且非編輯模式下顯示） */}
              {onRestoreCard && editingUid !== item._uid && editedItemIds?.has(item._uid) && (
                <button
                  className='card-edit-btn card-restore-btn'
                  onClick={e => {
                    e.stopPropagation();
                    onRestoreCard(item._uid);
                  }}
                  aria-label='還原 AI 建議'
                  title='還原 AI 建議'
                >
                  <ReloadOutlined />
                </button>
              )}
              {/* 筆圖示（正常模式下顯示；編輯過則變藍色） */}
              {onEditCard && editingUid !== item._uid && (
                <button
                  className='card-edit-btn'
                  onClick={e => {
                    e.stopPropagation();
                    setEditingUid(item._uid);
                    setEditDraft({
                      item_id: item.item_id,
                      rule: item.rule,
                      origin: item.origin,
                      suggestion: item.suggestion,
                    });
                  }}
                  aria-label='編輯此審閱建議'
                  title='編輯建議'
                  style={editedItemIds?.has(item._uid) ? { color: '#fa8c16' } : undefined}
                >
                  <EditOutlined />
                </button>
              )}
              {/* 刪除按鈕 */}
              {onDeleteCard && (
                <button
                  className='card-delete-btn'
                  onClick={e => {
                    e.stopPropagation();
                    if (editingUid === item._uid) setEditingUid(null);
                    onDeleteCard(item._uid);
                  }}
                  aria-label='刪除此審閱項目'
                  title='刪除此項目'
                >
                  <DeleteOutlined />
                </button>
              )}
              <div className='risk-item-header' style={{ paddingRight: 72 }}>
                <span className='risk-seq'>{originalIndex + 1}.</span>
                {editingUid === item._uid && ruleOptions && ruleOptions.length > 0 ? (
                  <Select
                    value={editDraft.item_id || null}
                    onChange={(val: string) => {
                      const opt = ruleOptions.find(r => r.id === val);
                      if (opt) setEditDraft(prev => ({ ...prev, item_id: opt.id, rule: opt.name }));
                    }}
                    onClick={e => e.stopPropagation()}
                    className='rule-edit-select'
                    style={{ width: 260 }}
                    showSearch
                    optionFilterProp='label'
                    options={ruleOptions.map(r => ({
                      value: r.id,
                      label: `第${r.id}條 ${r.name}`,
                    }))}
                  />
                ) : (
                  <span className='rule-label'>{ruleLabel}</span>
                )}
              </div>

              <div className='risk-item-content'>
                <div className='original-content'>
                  {editingUid === item._uid ? (
                    <Input.TextArea
                      autoSize={{ minRows: 1 }}
                      value={editDraft.origin}
                      onChange={e => setEditDraft(prev => ({ ...prev, origin: e.target.value }))}
                      onClick={e => e.stopPropagation()}
                    />
                  ) : (
                    <TextHighlight
                      text={`"${item.origin}"`}
                      ranges={createHighlightRanges(item.origin, riskLevel)}
                      onHighlightClick={() => handleJumpToText(item)}
                    />
                  )}
                </div>

                <div className='location-info'>
                  <span>
                    頁碼：{item.page}
                    {item.line && ` (行: ${item.line})`}
                  </span>
                  <Button
                    size='small'
                    className='jump-btn'
                    onClick={e => {
                      e.stopPropagation();
                      handleJumpToText(item);
                    }}
                  >
                    📍 定位
                  </Button>
                </div>

                <div className='suggestion-content'>
                  {!hideSuggestionLabel && <strong>建議：</strong>}
                  {editingUid === item._uid ? (
                    <Input.TextArea
                      autoSize={{ minRows: 2 }}
                      value={editDraft.suggestion}
                      onChange={e =>
                        setEditDraft(prev => ({ ...prev, suggestion: e.target.value }))
                      }
                      onKeyDown={e => {
                        if (e.key === 'Escape') {
                          e.stopPropagation();
                          setEditingUid(null);
                        }
                        if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
                          e.stopPropagation();
                          const changes: Partial<ReviewItem> = {};
                          if (editDraft.item_id.trim() && editDraft.item_id.trim() !== item.item_id)
                            changes.item_id = editDraft.item_id.trim();
                          if (editDraft.rule.trim() && editDraft.rule.trim() !== item.rule)
                            changes.rule = editDraft.rule.trim();
                          if (editDraft.origin.trim() && editDraft.origin.trim() !== item.origin)
                            changes.origin = editDraft.origin.trim();
                          if (
                            editDraft.suggestion.trim() &&
                            editDraft.suggestion.trim() !== item.suggestion
                          )
                            changes.suggestion = editDraft.suggestion.trim();
                          if (Object.keys(changes).length > 0) onEditCard!(item._uid, changes);
                          setEditingUid(null);
                        }
                      }}
                      onClick={e => e.stopPropagation()}
                      autoFocus
                      style={{ marginTop: 4 }}
                    />
                  ) : hideSuggestionLabel && parseSuggestion(item.suggestion) ? (
                    (() => {
                      const parsed = parseSuggestion(item.suggestion)!;
                      return (
                        <div className='comparison-suggestion'>
                          <div className='comparison-origin-block'>
                            <strong>原稿：</strong>
                            <span className='comparison-origin-text'>{parsed.origin}</span>
                          </div>
                          <div className='comparison-explanation-block'>
                            <strong>說明：</strong>
                            <span className='comparison-explanation-text'>{parsed.explanation}</span>
                          </div>
                        </div>
                      );
                    })()
                  ) : (
                    item.suggestion
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default ReviewResults;
